import { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/gallery';

const EVENT_TYPES = ['meeting', 'burial', 'agm', 'social', 'training', 'other'];

function Gallery() {
  // 'albums' = folder grid, 'album' = inside one album looking at its photos
  const [view, setView] = useState('albums');
  const [albums, setAlbums] = useState([]);
  const [activeAlbum, setActiveAlbum] = useState(null);

  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [albumForm, setAlbumForm] = useState({ title: '', description: '', event_date: '', event_type: 'meeting' });

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canUpload = ['admin', 'finance', 'secretary'].includes(user.role);

  useEffect(() => { fetchAlbums(); }, []);

  const fetchAlbums = () => {
    axios.get(`${API}/albums`).then(r => setAlbums(r.data)).catch(() => {});
  };

  const openAlbum = (id) => {
    axios.get(`${API}/albums/${id}`)
      .then(r => { setActiveAlbum(r.data); setView('album'); })
      .catch(() => setError('Failed to open album'));
  };

  const refreshActiveAlbum = () => {
    if (!activeAlbum) return;
    axios.get(`${API}/albums/${activeAlbum.id}`).then(r => setActiveAlbum(r.data)).catch(() => {});
  };

  const backToAlbums = () => {
    setView('albums');
    setActiveAlbum(null);
    setShowUploadForm(false);
    setFiles([]); setPreviews([]);
    fetchAlbums();
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!albumForm.title) return setError('Album title required');
    try {
      const res = await axios.post(`${API}/albums`, albumForm);
      setShowAlbumForm(false);
      setAlbumForm({ title: '', description: '', event_date: '', event_type: 'meeting' });
      // Open the new album immediately so uploads can start right away.
      setActiveAlbum({ ...res.data, photos: [] });
      setView('album');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create album');
    }
  };

  const handleDeleteAlbum = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this album and everything in it? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/albums/${id}`);
      fetchAlbums();
    } catch (err) {
      setError('Failed to delete album');
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(selected);
    // Preview thumbnails for every selected image; videos just show their
    // filename since there's nothing cheap to render before upload.
    Promise.all(selected.map(f => new Promise(resolve => {
      if (f.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ name: f.name, url: ev.target.result });
        reader.readAsDataURL(f);
      } else {
        resolve({ name: f.name, url: null });
      }
    }))).then(setPreviews);
  };

  // Uploads always go into the currently open album — pick many files here,
  // then fire one request per file in sequence, each becoming its own
  // gallery row (auto-numbered so titles don't collide).
  const handleUpload = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!files.length) return setError('Please select at least one file');
    if (!activeAlbum) return setError('No album open');
    setLoading(true);
    const multiple = files.length > 1;
    let uploadedCount = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length });
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('title', multiple ? `${activeAlbum.title} — Photo ${i + 1}` : activeAlbum.title);
        formData.append('album_id', activeAlbum.id);
        formData.append('event_date', activeAlbum.event_date || '');
        formData.append('event_type', activeAlbum.event_type || 'meeting');
        await axios.post(API, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedCount++;
      }
      setSuccess(multiple ? `Uploaded all ${uploadedCount} files successfully` : 'Uploaded successfully');
      setShowUploadForm(false);
      setFiles([]); setPreviews([]);
    } catch (err) {
      setError(
        (uploadedCount > 0 ? `Uploaded ${uploadedCount} of ${files.length} before this one failed: ` : '') +
        (err.response?.data?.error || 'Upload failed')
      );
    }
    setUploadProgress(null);
    setLoading(false);
    refreshActiveAlbum(); // refresh regardless — shows whatever did make it through
  };

  // Plain <a download> is unreliable cross-origin (frontend and backend
  // are on different Render domains — most browsers ignore the download
  // attribute and just navigate to the file instead). Fetching it as a
  // blob first and downloading that local object URL works regardless
  // of origin, and lets the saved file be named after the photo's own
  // title instead of the random server filename.
  const handleDownload = async (item) => {
    try {
      const response = await axios.get(`${API}/file/${item.file_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const ext = (item.file_path.split('.').pop() || '').toLowerCase();
      const safeName = (item.title || 'gallery-item').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed');
    }
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/${id}`);
      refreshActiveAlbum();
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const filteredAlbums = filter === 'all' ? albums : albums.filter(a => a.event_type === filter);

  return (
    <div style={{ padding: '20px' }}>
      {error   && <div style={alertStyle('#e74c3c')}>{error}   <span onClick={() => setError('')}   style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success} <span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {view === 'albums' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h2 style={{ color: '#2c3e50', margin: 0 }}>Gallery</h2>
              <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '4px 0 0' }}>Association photo &amp; video albums</p>
            </div>
            {canUpload && (
              <button onClick={() => setShowAlbumForm(!showAlbumForm)} style={btnStyle('#2c3e50')}>
                {showAlbumForm ? 'Cancel' : '+ New Album'}
              </button>
            )}
          </div>

          {showAlbumForm && canUpload && (
            <form onSubmit={handleCreateAlbum} style={formStyle}>
              <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>New Album</h3>
              <div style={fieldStyle}>
                <label style={labelStyle}>Title *</label>
                <input value={albumForm.title} onChange={e => setAlbumForm({ ...albumForm, title: e.target.value })} style={inputStyle} required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Event Type</label>
                <select value={albumForm.event_type} onChange={e => setAlbumForm({ ...albumForm, event_type: e.target.value })} style={inputStyle}>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Event Date</label>
                <input type="date" value={albumForm.event_date} onChange={e => setAlbumForm({ ...albumForm, event_date: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ ...fieldStyle, minWidth: '300px' }}>
                <label style={labelStyle}>Description</label>
                <input value={albumForm.description} onChange={e => setAlbumForm({ ...albumForm, description: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                <button type="submit" style={btnStyle('#2c3e50')}>Create &amp; Open</button>
                <button type="button" onClick={() => setShowAlbumForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}

          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {['all', ...EVENT_TYPES].map(t => (
              <button key={t} onClick={() => setFilter(t)} style={{
                padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '12px',
                background: filter === t ? '#2c3e50' : '#ecf0f1',
                color: filter === t ? 'white' : '#2c3e50',
                borderRadius: '20px'
              }}>{t.charAt(0).toUpperCase() + t.slice(1)} {t === 'all' ? `(${albums.length})` : `(${albums.filter(a => a.event_type === t).length})`}</button>
            ))}
          </div>

          {filteredAlbums.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#95a5a6' }}>No albums yet</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
            {filteredAlbums.map(album => (
              <div key={album.id} onClick={() => openAlbum(album.id)} style={{
                background: 'white', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #ecf0f1'
              }}>
                {album.cover_file_path_resolved ? (
                  album.cover_file_type === 'video' ? (
                    <video src={`${API}/file/${album.cover_file_path_resolved}`} style={{ width: '100%', height: '160px', objectFit: 'cover' }} />
                  ) : (
                    <img src={`${API}/file/${album.cover_file_path_resolved}`} alt={album.title} style={{ width: '100%', height: '160px', objectFit: 'cover' }} />
                  )
                ) : (
                  <div style={{ width: '100%', height: '160px', background: '#ecf0f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>📁</div>
                )}
                <div style={{ padding: '12px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#2c3e50', marginBottom: '4px' }}>{album.title}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ background: '#ecf0f1', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', color: '#7f8c8d' }}>
                        {album.event_type}
                      </span>
                      <span style={{ fontSize: '11px', color: '#95a5a6', marginLeft: '6px' }}>
                        {album.photo_count} item{album.photo_count === '1' ? '' : 's'}
                      </span>
                    </div>
                    {canUpload && (
                      <button onClick={(e) => handleDeleteAlbum(album.id, e)} title="Delete Album" style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }}>🗑</button>
                    )}
                  </div>
                  {album.event_date && (
                    <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '4px' }}>{album.event_date.slice(0, 10)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {view === 'album' && activeAlbum && (
        <>
          <button onClick={backToAlbums} style={{ ...btnStyle('#95a5a6'), marginBottom: '16px' }}>← Back to Albums</button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h2 style={{ color: '#2c3e50', margin: 0 }}>{activeAlbum.title}</h2>
              <div style={{ marginTop: '4px' }}>
                <span style={{ background: '#ecf0f1', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', color: '#7f8c8d' }}>
                  {activeAlbum.event_type}
                </span>
                {activeAlbum.event_date && (
                  <span style={{ fontSize: '11px', color: '#95a5a6', marginLeft: '6px' }}>{activeAlbum.event_date.slice(0, 10)}</span>
                )}
              </div>
              {activeAlbum.description && (
                <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '6px 0 0' }}>{activeAlbum.description}</p>
              )}
            </div>
            {canUpload && (
              <button onClick={() => setShowUploadForm(!showUploadForm)} style={btnStyle('#2c3e50')}>
                {showUploadForm ? 'Cancel' : '+ Upload'}
              </button>
            )}
          </div>

          {showUploadForm && canUpload && (
            <form onSubmit={handleUpload} style={formStyle}>
              <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>Upload Photo / Video</h3>
              <div style={fieldStyle}>
                <label style={labelStyle}>File(s) (JPG/PNG/GIF/MP4) *</label>
                <input type="file" accept=".jpg,.jpeg,.png,.gif,.mp4,.mov" multiple onChange={handleFileChange} style={inputStyle} required />
                {files.length > 1 && (
                  <span style={{ fontSize: '11px', color: '#7f8c8d' }}>
                    {files.length} files selected — each uploads as its own item into "{activeAlbum.title}"
                  </span>
                )}
              </div>
              {previews.length > 0 && (
                <div style={{ width: '100%', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {previews.map((p, i) => (
                    p.url
                      ? <img key={i} src={p.url} alt={p.name} style={{ height: '80px', borderRadius: '6px', border: '1px solid #ddd' }} />
                      : (
                        <div key={i} style={{
                          height: '80px', width: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '10px', color: '#7f8c8d', border: '1px solid #ddd', borderRadius: '6px',
                          padding: '4px', textAlign: 'center', overflow: 'hidden'
                        }}>{p.name}</div>
                      )
                  ))}
                </div>
              )}
              <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
                <button type="submit" style={btnStyle('#2c3e50')} disabled={loading}>
                  {loading
                    ? (uploadProgress ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}...` : 'Uploading...')
                    : (files.length > 1 ? `Upload ${files.length} Files` : 'Upload')}
                </button>
                <button type="button" onClick={() => setShowUploadForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
              </div>
            </form>
          )}

          {(!activeAlbum.photos || activeAlbum.photos.length === 0) && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#95a5a6' }}>No items in this album yet</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
            {(activeAlbum.photos || []).map(item => (
              <div key={item.id} style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #ecf0f1' }}>
                {item.file_type === 'image' ? (
                  <img
                    src={`${API}/file/${item.file_path}`}
                    alt={item.title}
                    style={{ width: '100%', height: '180px', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => window.open(`${API}/file/${item.file_path}`, '_blank')}
                  />
                ) : (
                  <video
                    src={`${API}/file/${item.file_path}`}
                    style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                    controls
                  />
                )}
                <div style={{ padding: '12px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#2c3e50', marginBottom: '4px' }}>{item.title}</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button onClick={() => handleDownload(item)} title="Download" style={{ background: 'none', border: 'none', color: '#2c3e50', cursor: 'pointer', fontSize: '16px' }}>⬇</button>
                    {canUpload && (
                      <button onClick={() => handleDeletePhoto(item.id)} title="Delete" style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '16px', marginLeft: '6px' }}>🗑</button>
                    )}
                  </div>
                  {item.description && (
                    <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px' }}>{item.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const btnStyle  = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' });
const formStyle = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '180px' };
const labelStyle = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const alertStyle = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });

export default Gallery;
